import { createFileRoute } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { DownloadIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { EnrollmentStatusBadge } from "@/components/enrollment-status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { useAuth } from "@/hooks/auth/auth";
import { downloadAuthenticatedFile } from "@/lib/download-file";
import { findEnumName } from "@/lib/enum-utils";
import { Gender, type TeacherClassRosterItemDTO } from "@/orval/model";
import { useOrganizationTeacherGetOrganizationSettings } from "@/orval/organization-teacher";
import {
  getTeacherClassesGetTeacherClassRosterQueryKey,
  teacherClassesGetTeacherClassRoster,
  useTeacherClassesGetTeacherClassDetail,
} from "@/orval/teacher-classes";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { useState } from "react";
import { toast } from "sonner";

const blockedRosterTitle = "Roster locked until registration closes";

function getBlockedRosterDescription(rosterAvailableAfter?: string | null) {
  if (!rosterAvailableAfter) {
    return "Roster access will become available after registration closes for this semester.";
  }

  return `Roster access will become available after ${format(parseISO(rosterAvailableAfter), "MMMM d, yyyy")}.`;
}

const studentInfoRosterColumns: CustomColumnDef<TeacherClassRosterItemDTO>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date of Birth" />,
    accessorFn: (row) => format(parseISO(row.dateOfBirth), "MM/dd/yyyy"),
    id: "dateOfBirth",
    sortField: "Student.Child.Birthday",
  },
  {
    header: "Additional Info / Allergies",
    accessorFn: (row) => row.additionalInformation?.trim() ?? "",
    id: "additionalInformation",
    cell: ({ row }) => {
      const value = row.original.additionalInformation?.trim();

      if (!value) {
        return <span className="text-muted-foreground">-</span>;
      }

      return (
        <span className="block max-w-[18rem] truncate text-sm text-muted-foreground" title={value}>
          {value}
        </span>
      );
    },
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Gender" />,
    accessorFn: (row) => findEnumName(Gender, row.gender),
    id: "gender",
    sortField: "Student.Child.Gender",
  },
];

const waitlistRosterColumns: CustomColumnDef<TeacherClassRosterItemDTO>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    accessorKey: "status",
    cell: ({ row }) => (row.original.status ? <EnrollmentStatusBadge status={row.original.status} /> : "-"),
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Waitlist #" />,
    accessorFn: (row) => row.waitlistPosition ?? "-",
    id: "waitlistPosition",
  },
];

export const Route = createFileRoute("/_app/organizations/$organizationId/teacher/classes_/$classId/roster")({
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId, classId } = Route.useParams();
  const { token } = useAuth();
  const [isExportingRoster, setIsExportingRoster] = useState(false);
  const { data: classDetail, isLoading: isClassLoading } = useTeacherClassesGetTeacherClassDetail(
    organizationId,
    classId,
  );
  const { data: teacherSettings } = useOrganizationTeacherGetOrganizationSettings(organizationId);

  const exportRoster = async () => {
    if (!token) {
      toast.error("Sign in again to export the roster.");
      return;
    }

    setIsExportingRoster(true);

    try {
      await downloadAuthenticatedFile({
        url: `/api/organizations/${organizationId}/teacher/TeacherClasses/${classId}/export`,
        token,
        fallbackFilename: "class_roster.xlsx",
      });
    } catch (error) {
      console.error("Error exporting roster:", error);
      toast.error("Unable to export roster.");
    } finally {
      setIsExportingRoster(false);
    }
  };

  const rosterColumns: CustomColumnDef<TeacherClassRosterItemDTO>[] = [
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      accessorFn: (row) => `${row.studentFirstName} ${row.studentLastName}`,
      id: "studentFullName",
      sortField: "Student.Child.FullName",
      cell: ({ row }) => <span className="font-medium">{row.getValue("studentFullName")}</span>,
    },
    ...studentInfoRosterColumns,
    ...(teacherSettings?.canTeachersViewWaitlistedStudents ? waitlistRosterColumns : []),
    {
      header: "Parent 1",
      accessorKey: "parent1Name",
    },
    {
      header: "Parent 1 Email",
      accessorFn: (row) => row.parent1Email ?? "-",
      id: "parent1Email",
    },
    {
      header: "Parent 1 Phone",
      accessorFn: (row) => row.parent1Phone ?? "-",
      id: "parent1Phone",
    },
    {
      header: "Parent 2",
      accessorFn: (row) => row.parent2Name ?? "-",
      id: "parent2Name",
    },
    {
      header: "Parent 2 Email",
      accessorFn: (row) => row.parent2Email ?? "-",
      id: "parent2Email",
    },
    {
      header: "Parent 2 Phone",
      accessorFn: (row) => row.parent2Phone ?? "-",
      id: "parent2Phone",
    },
  ];

  const renderRoster = () => {
    if (isClassLoading) {
      return <Skeleton className="h-32 w-full" />;
    }
    if (classDetail?.isRosterAccessBlocked) {
      return (
        <Alert>
          <AlertTitle>{blockedRosterTitle}</AlertTitle>
          <AlertDescription>{getBlockedRosterDescription(classDetail.rosterAvailableAfter)}</AlertDescription>
        </Alert>
      );
    }
    return classDetail ? (
      <DataTable
        className="min-w-0 overflow-hidden"
        columns={rosterColumns}
        queryFn={(params, signal) => teacherClassesGetTeacherClassRoster(organizationId, classId, params, signal)}
        queryKeyFn={(params) => getTeacherClassesGetTeacherClassRosterQueryKey(organizationId, classId, params)}
        getRowIdFn={(row) => row.id.toString()}
      />
    ) : null;
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Roster</h2>
          <p className="text-sm text-muted-foreground">View enrolled students and family contact information.</p>
        </div>
        {classDetail?.isRosterAccessBlocked ? (
          <Button variant="accent" size="sm" disabled title={blockedRosterTitle}>
            <DownloadIcon className="mr-2 size-4" />
            Export Roster
          </Button>
        ) : (
          <Button
            variant="accent"
            size="sm"
            title="Export roster"
            disabled={isExportingRoster}
            onClick={() => void exportRoster()}
          >
            <DownloadIcon className="mr-2 size-4" />
            {isExportingRoster ? "Exporting..." : "Export Roster"}
          </Button>
        )}
      </div>
      {renderRoster()}
    </>
  );
}
